import { createContext, useState, useContext, useRef, useEffect, useMemo, SetStateAction, Dispatch } from "react";
import { UserContext } from "../contexts/UserContext";
import { SearchNotOrdered } from "./types";
import { MRT_ColumnDef, MRT_ColumnFiltersState } from "material-react-table";
import axios from "axios";
import { processSearchStatuses } from "./processSearchStatuses";
import { getColumns } from "./columns";
import { NotesColumn } from "../reports/NotesColumn";

type ContextType = {
  isLoading: boolean;
  searchesNotOrdered: SearchNotOrdered[];
  setSearchesNotOrdered: Dispatch<SetStateAction<SearchNotOrdered[]>>;
  selectedRow: SearchNotOrdered | null;
  setSelectedRow: Dispatch<SetStateAction<SearchNotOrdered | null>>;
  selectedEditRowStatus: SearchNotOrdered | null;
  setSelectedEditRowStatus: Dispatch<SetStateAction<SearchNotOrdered | null>>;
  selectedEditRowETA: SearchNotOrdered | null;
  setSelectedEditRowETA: Dispatch<SetStateAction<SearchNotOrdered | null>>;
  selectedStatuses: string[];
  setSelectedStatuses: Dispatch<SetStateAction<string[]>>;
  assignedToMeOnly: boolean;
  setAssignedToMeOnly: Dispatch<SetStateAction<boolean>>;
  unassignedOnly: boolean;
  setUnassignedOnly: Dispatch<SetStateAction<boolean>>;
  outstandingOnly: boolean;
  setOutstandingOnly: Dispatch<SetStateAction<boolean>>;
  columnFilters: MRT_ColumnFiltersState;
  setColumnFilters: Dispatch<SetStateAction<MRT_ColumnFiltersState>>;
  columnsWithNotes: MRT_ColumnDef<SearchNotOrdered, unknown>[];
  filteredSearchesNotOrdered: SearchNotOrdered[];
  getRowKey: (r: SearchNotOrdered) => string;
  newRows: Set<string>;
};

const SearchesNotOrderedContext = createContext<ContextType | null>(null);

const defaultFilters: MRT_ColumnFiltersState = [
  {
    id: "frontendStatus",
    value: ["Overdue", "Due", "Required", "Awaiting ETA", "ETA Upcoming", "Waiting for Client"]
  },
  { id: "department", value: ["National", "NJ/PA"] }
];

export function SearchesNotOrderedProvider({ children }: { children: React.ReactNode }) {
  const { user } = useContext(UserContext);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [searchesNotOrdered, setSearchesNotOrdered] = useState<SearchNotOrdered[]>([]);
  const [selectedRow, setSelectedRow] = useState<SearchNotOrdered | null>(null);
  const [selectedEditRowStatus, setSelectedEditRowStatus] = useState<SearchNotOrdered | null>(null);
  const [selectedEditRowETA, setSelectedEditRowETA] = useState<SearchNotOrdered | null>(null);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>(["Required", "Waiting for Client"]);
  const [assignedToMeOnly, setAssignedToMeOnly] = useState<boolean>(() => {
    const savedAssignedToMeOnly = localStorage.getItem("searchesNotOrdered_assignedToMeOnly");
    if (!savedAssignedToMeOnly) return true;
    return JSON.parse(savedAssignedToMeOnly);
  });
  const [unassignedOnly, setUnassignedOnly] = useState<boolean>(() => {
    const savedUnassignedOnly = localStorage.getItem("searchesNotOrdered_unassignedOnly");
    if (!savedUnassignedOnly) return false;
    return JSON.parse(savedUnassignedOnly);
  });
  const [outstandingOnly, setOutstandingOnly] = useState<boolean>(false);
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>(() => {
    const savedFilters = localStorage.getItem("searchesNotOrdered_columnFilters");
    if (!savedFilters) return defaultFilters;

    try {
      const parsed = JSON.parse(savedFilters);
      return parsed.filter((filter: any) => filter.id !== "orderNumberWithPrefix");
    } catch {
      return defaultFilters;
    }
  });

  const initialOrderNumbersRef = useRef<Set<string> | null>(null);
  const [newRows, setNewRows] = useState<Set<string>>(new Set());
  const getRowKey = (r: SearchNotOrdered) => r.orderNumber;

  const latestRequestIdRef = useRef(0);
  const mountedRef = useRef(true);
  const REFRESH_INTERVAL_SECONDS = 60;

  const refreshData = async (showLoader = false, signal?: AbortSignal) => {
    latestRequestIdRef.current += 1;
    const reqId = latestRequestIdRef.current;

    if (showLoader) {
      setIsLoading(true);
    }

    try {
      const { data } = await axios.get<SearchNotOrdered[]>("/api/outstandingChecks/searchesNotOrdered", { signal });
      const processedData = processSearchStatuses(data);
      const incomingOrderNumbers = new Set(processedData.map(getRowKey));
      if (!initialOrderNumbersRef.current) {
        initialOrderNumbersRef.current = new Set(incomingOrderNumbers);
      } else {
        const originalOrderNumberSet = initialOrderNumbersRef.current;
        const newlySeen = [...incomingOrderNumbers].filter((orderNumber) => !originalOrderNumberSet!.has(orderNumber));
        if (newlySeen.length > 0) {
          setNewRows((prev) => new Set([...prev, ...newlySeen]));
        }
      }

      setSearchesNotOrdered(processedData);
      if (mountedRef.current && reqId === latestRequestIdRef.current) {
        setSearchesNotOrdered(processedData);
      }
    } catch (err: any) {
      if (!(axios.isAxiosError(err) && err.code === "ERR_CANCELED")) {
        console.error(err);
      }
    } finally {
      if (mountedRef.current && reqId === latestRequestIdRef.current) {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    mountedRef.current = true;

    let cancelled = false;
    const controller = new AbortController();
    refreshData(true, controller.signal);
    const id = setInterval(() => refreshData(false, controller.signal), REFRESH_INTERVAL_SECONDS * 1000);

    return () => {
      cancelled = true;
      mountedRef.current = false;
      clearInterval(id);
      controller.abort();
    };
  }, []);

  const columns = useMemo(() => getColumns({ setSelectedEditRowStatus, setSelectedEditRowETA }), []);
  const columnsWithNotes = NotesColumn<SearchNotOrdered>(columns, setSelectedRow);

  const filteredSearchesNotOrdered = useMemo(() => {
    const filtered = searchesNotOrdered.filter(
      (o) =>
        ((assignedToMeOnly && o?.assignedToEmail?.toLowerCase() === user?.email?.toLowerCase()) || !assignedToMeOnly) &&
        ((unassignedOnly && !o?.assignedTo && !o.assignedToUser) || !unassignedOnly)
    );
    const orderMap: Record<string, number> = {
      Overdue: 1,
      Due: 2,
      Required: 3,
      "Awaiting ETA": 4,
      "ETA Upcoming": 5,
      "Waiting for Client": 7
    };

    return filtered.sort((a, b) => {
      const aOrder = orderMap[a.status || ""] ?? 6;
      const bOrder = orderMap[b.status || ""] ?? 6;
      return aOrder - bOrder;
    });
  }, [searchesNotOrdered, selectedStatuses, assignedToMeOnly, unassignedOnly, outstandingOnly]);

  useEffect(() => {
    localStorage.setItem("searchesNotOrdered_assignedToMeOnly", JSON.stringify(assignedToMeOnly));
  }, [assignedToMeOnly]);

  useEffect(() => {
    localStorage.setItem("searchesNotOrdered_unassignedOnly", JSON.stringify(unassignedOnly));
  }, [unassignedOnly]);

  useEffect(() => {
    localStorage.setItem("searchesNotOrdered_columnFilters", JSON.stringify(columnFilters));
  }, [columnFilters]);

  useEffect(() => {
    if (outstandingOnly) {
      setColumnFilters([{ id: "frontendStatus", value: ["Ordered"] }]);
    }
  }, [outstandingOnly]);

  return (
    <SearchesNotOrderedContext.Provider
      value={{
        isLoading,
        searchesNotOrdered,
        setSearchesNotOrdered,
        selectedRow,
        setSelectedRow,
        selectedEditRowStatus,
        setSelectedEditRowStatus,
        selectedEditRowETA,
        setSelectedEditRowETA,
        selectedStatuses,
        setSelectedStatuses,
        assignedToMeOnly,
        setAssignedToMeOnly,
        unassignedOnly,
        setUnassignedOnly,
        outstandingOnly,
        setOutstandingOnly,
        columnFilters,
        setColumnFilters,
        columnsWithNotes,
        filteredSearchesNotOrdered,
        getRowKey,
        newRows
      }}
    >
      {children}
    </SearchesNotOrderedContext.Provider>
  );
}

export function useSearchesNotOrdered() {
  const ctx = useContext(SearchesNotOrderedContext);
  if (!ctx) throw new Error("useSearchesNotOrderedContext must be used within a SearchesNotOrderedProvider");
  return ctx;
}
